import { FirebaseError } from "firebase/app";
import backendConfig from "../config/backendConfig.ts";
import { OrderStatus } from "../models/order-status.js";
import { Order } from "../models/order.js";

// Helper function to make API requests
const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const url = `${backendConfig.API_BASE_URL}${endpoint}`;
  
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

export const createOrder = async (
  orderData: Order
): Promise<{ success: boolean; id?: string; error?: FirebaseError }> => {
  try {
    const result = await apiRequest(backendConfig.ENDPOINTS.ORDERS, {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
    
    if (!result.success) {
      return { success: false, error: new Error(result.error) as FirebaseError };
    }
    
    return { success: true, id: result.id };
  } catch (error: any) {
    console.error("Creating order failed:", error);
    return { success: false, error: error as FirebaseError };
  }
};

export const getOrders = async (): Promise<{
  success: boolean;
  data?: Order[];
  error?: FirebaseError;
}> => {
  try {
    const result = await apiRequest(backendConfig.ENDPOINTS.ORDERS);
    
    if (!result.success) {
      return { success: false, error: new Error(result.error) as FirebaseError };
    }
    
    const orders: Order[] = result.data.map((order: any) => ({
      ...order,
      id: order._id,
      viewed: order.viewed === true,
      createdAt: new Date(order.createdAt),
      updatedAt: new Date(order.updatedAt),
    })) as Order[];
    
    return { success: true, data: orders };
  } catch (error: any) {
    console.error("Fetching orders failed:", error);
    return { success: false, error: error as FirebaseError };
  }
};

export const changeOrderStatus = async (
  orderId: string,
  newStatus: OrderStatus
): Promise<{ success: boolean; error?: FirebaseError | string }> => {
  try {
    const result = await apiRequest(`${backendConfig.ENDPOINTS.ORDERS}/${orderId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status: newStatus }),
    });
    
    if (!result.success) {
      return { success: false, error: result.error };
    }
    
    return { success: true };
  } catch (error: any) {
    console.error("Changing order status failed:", error);
    return { success: false, error: error.message };
  }
};

export const markOrderViewed = async (
  orderId: string,
): Promise<{ success: boolean; error?: string }> => {
  try {
    const result = await apiRequest(
      `${backendConfig.ENDPOINTS.ORDERS}/${orderId}/viewed`,
      {
        method: "PATCH",
        body: JSON.stringify({ viewed: true }),
      },
    );

    if (!result.success) {
      return { success: false, error: result.error };
    }

    return { success: true };
  } catch (error: any) {
    console.error("Marking order viewed failed:", error);
    return { success: false, error: error.message };
  }
};

export const deleteOrder = async (
  orderId: string,
  firebaseId?: string
): Promise<{ success: boolean; error?: FirebaseError | string }> => {
  try {
    const documentId = firebaseId || orderId;
    const result = await apiRequest(`${backendConfig.ENDPOINTS.ORDERS}/${documentId}`, {
      method: 'DELETE',
    });
    
    if (!result.success) {
      return { success: false, error: result.error };
    }
    
    return { success: true };
  } catch (error: any) {
    console.error("Deleting order failed:", error);
    return { success: false, error: error.message };
  }
};
